/* eslint-disable react/jsx-one-expression-per-line */
import { Formik } from 'formik';
import * as yup from 'yup';
import React from 'react';
import { StatusCodes } from 'http-status-codes';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { v4 as uuid } from 'uuid';
import FileInputComponent from '../../../../components/common/forms/FileInput/FileInputComponent';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import { UPLOAD_MAX_SIZE_IMAGE, FILE_UPLOAD_TYPES } from '../../../../utils/constants';
import FilesApi from '../../../../services/api/FilesApi';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import ResultDtoDisplay from '../../../imports/components/ResultDtoDisplay';
import InventoryApi from '../../../../services/api/InventoryApi';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import { ResultDto } from 'svila-common';
import styled from 'styled-components';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import { Colors } from '~/src/scss/common/style';
import uploadIcon from '~/src/static/img/upload.svg';
import DataOperationsNav from '~/src/components/common/navigation/DataOperationsNav';
import InventoryExportApi from '~/src/services/api/InventoryExportApi';

const validationSchema = yup.object().shape({
  certificate: yup.mixed().test('fileSize', 'Моля използвайте файлове до 3MB', (value) => {
    if (typeof value === 'undefined' || value.length <= 0) {
      return true;
    }
    return value[0].size <= UPLOAD_MAX_SIZE_IMAGE;
  }),
});

export default function InventoryImportExport(): JSX.Element {
  const [uuidExport, setUuidExport] = React.useState<string | null>(null);
  const [result, setResult] = React.useState(null);
  const [exportResult, setExportResult] = React.useState<ResultDto | null>(null);
  const [isExporting, setIsExporting] = React.useState(false);
  const intervalId = React.useRef<number | null>(null);
  const history = useHistory();

  const onSubmitMine = async (values, setSubmitting) => {
    setSubmitting(true);
    const files = values.xlsx;

    if (files && files.length > 0) {
      const fileData = { files, type: FILE_UPLOAD_TYPES.PRODUCT_IMPORTS };
      var fileUploadResp = await FilesApi.create(fileData);

      if (fileUploadResp.status === StatusCodes.OK) {
        var res = await InventoryApi.importMovementsFromExcel(fileUploadResp.data[0].id);

        if (res.status === StatusCodes.OK) {
          setResult(res.data);
        }
      }
    }

    setSubmitting(false);
  };

  const onExportQuantities = async (type: string): Promise<void> => {
    const uuidY = uuid();
    setUuidExport(uuidY);
    setIsExporting(true);
    setExportResult(null);
    const x = await InventoryExportApi.startExport(uuidY, type);

    if (x.status === StatusCodes.OK) {
      setExportResult(x.data);
      if (x.data.result == false) {
        setIsExporting(false);
      } else {
        intervalId.current = window.setInterval(() => {
          intervalCheck(uuidY);
        }, 5000);
      }
    } else if (x.status === StatusCodes.BAD_REQUEST) {
      setIsExporting(false);
    }
  };

  const intervalCheck = async (uuidX: string): Promise<void> => {
    const x = await InventoryExportApi.checkAndDownloadExport(uuidX);

    if (x.status === StatusCodes.OK) {
      setIsExporting(false);
      const courseSheet = x.data;

      if (courseSheet) {
        const blob = new Blob([courseSheet], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        saveAs(blob, `export-quantities.xlsx`);
      }

      if (intervalId.current) {
        clearInterval(intervalId.current);
        setUuidExport(null);
      }

      setExportResult(null);
    } else if (x.status === StatusCodes.ACCEPTED) {
      // pass, wait for next interval
    } else {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <WhitePad>
        <div style={{ display: 'flex' }}>
          <DataOperationsNav />
          <div>
            <TitleComponent title="Inventory"
              titleLeft="Back"
              onLeftHandler={(): void => history.goBack()}
            />
            <FormWrapper width="100%">
              <div>
                <Title>Export</Title>
                <ActionWrapper>
                  <PrimaryButton
                    onClick={() => {
                      onExportQuantities('Full');
                    }}
                    size="sm"
                    isSubmitting={isExporting}
                    title="Export Full Quantities"
                  />
                  <PrimaryButton
                    onClick={() => {
                      onExportQuantities('Gomba');
                    }}
                    size="sm"
                    isSubmitting={isExporting}
                    title="Export Gomba Quantities"
                  />
                </ActionWrapper>
              </div>
            </FormWrapper>
            <ResultDtoDisplay result={exportResult} />
            {/* <PrimaryButton onClick={onFixDates} size="sm" isSubmitting={isFixingDates} title="Fix Dates" /> */}
            <FormWrapper>
              <div>
                <Title>Instructions</Title>
                <StyleP>The excel needs to have the following columns in that specific order:</StyleP>
                <Table>
                  <tbody>
                    <tr>
                      <StyleTd>
                        [A] <b>CuGtin</b>
                      </StyleTd>
                      <StyleTd>used only for search. Will not change database.</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [B] <b>DpGtin</b>
                      </StyleTd>
                      <StyleTd>NOT used for search. Will update database if not empty.</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [C] <b>DuGtin</b>
                      </StyleTd>
                      <StyleTd>NOT used for search. Will update database if not empty.</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [D] <b>Name</b>
                      </StyleTd>
                      <StyleTd>
                        NOT USED FOR ANYTHING, only to make preparing of excel more visual - will not update
                        database.
                      </StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [E] <b>ExpirationDate</b>
                      </StyleTd>
                      <StyleTd>format DD/MM/YYYY. Ex: 13/04/2022</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [F] <b>Room</b> (0-9)
                      </StyleTd>
                      <StyleTd>the room number in arabic numerals. Please do not put Roman Numerals.</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [G] <b>Column</b>
                      </StyleTd>
                      <StyleTd>number of the column</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [H] <b>Letter</b>
                      </StyleTd>
                      <StyleTd>letter of the row down the column</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [I] <b>Level</b>
                      </StyleTd>
                      <StyleTd>vertical level. Starts from 1 and goes upwards</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [J] <b>FullDuUnits</b>
                      </StyleTd>
                      <StyleTd></StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [K] <b>SingleDpUnits</b>
                      </StyleTd>
                      <StyleTd></StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [L] <b>SingleCuUnits</b>
                      </StyleTd>
                      <StyleTd></StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [M] <b>OrderDate</b>
                      </StyleTd>
                      <StyleTd>format DD/MM/YYYY. Ex: 13/04/2022. Not imported as of now.</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [N] <b>OrderCustomer</b>
                      </StyleTd>
                      <StyleTd>name of the customer. Not imported as of now.</StyleTd>
                    </tr>
                    <tr>
                      <StyleTd>
                        [O] <b>SupplierName</b>
                      </StyleTd>
                      <StyleTd>
                        Leave empty unless you have more than one supplier and you need to choose. Error below will
                        let you know for which products this is required.
                      </StyleTd>
                    </tr>
                  </tbody>
                </Table>
                <Formik
                  initialValues={{
                    xlsx: [],
                  }}
                  validationSchema={validationSchema}
                  onSubmit={(values, { setSubmitting }) => onSubmitMine(values, setSubmitting)}
                >
                  {({ values, handleChange, handleBlur, isSubmitting, setFieldValue, onSubmit, handleSubmit }) => (
                    <form autoComplete="off" onSubmit={handleSubmit} style={{ marginTop: '30px' }}>
                      <FormRow>
                        <FileInputComponent
                          name="xlsx"
                          value={values.xlsx}
                          onBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          cols={12}
                        />
                      </FormRow>
                      <div style={{ marginTop: '40px' }}>
                        <SecondaryButton
                          icon={uploadIcon}
                          type="submit"
                          isSubmitting={isSubmitting}
                          title="Upload"
                        />
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </FormWrapper>
            <ResultDtoDisplay result={result} />
          </div>
        </div>
      </WhitePad>
    </div>
  );
}

const Table = styled.div`
  border: 1px solid #fff;
  border-radius: 10px;
  overflow: hidden;
`;

const StyleTd = styled.td`
  width: 300px;
  border: 1px solid #fff;
  padding: 7px;
  font-size: 14px;
`;

const StyleP = styled.p`
  font-size: 14px;
  line-height: 17pxl
  color: #343434;
  text-align: center;
  margin-bottom: 43px;
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 20px;
  line-height: 23px;
  text-align: center;
  text-transform: uppercase;
  color: ${Colors.darkBrown};
`;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
`;
